import { useMutation } from '@apollo/client';
import type { UpdateObligationAssessmentResultMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { UpdateObligationAssessmentResultDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import type { UpdateObligationAssessmentResultTRPCInput } from './useUpdateObligationAssessmentResultTRPC';
import { useUpdateObligationAssessmentResultTRPC } from './useUpdateObligationAssessmentResultTRPC';

export const useUpdateObligationAssessmentResult = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  // GraphQL mutation
  const [updateObligationAssessmentResultGraphQL, graphqlState] = useMutation(
    UpdateObligationAssessmentResultDocument,
    {
      update: (cache) => {
        evictField(cache, 'obligation_assessment_result');
        evictField(cache, 'assessment');
        evictField(cache, 'obligation_assessment_result_aggregate');
      },
    }
  );

  // tRPC mutation
  const trpcMutation = useUpdateObligationAssessmentResultTRPC();

  const updateObligationAssessmentResult = async (
    variables: UpdateObligationAssessmentResultTRPCInput
  ): Promise<UpdateObligationAssessmentResultMutation> => {
    if (trpcEnabled) {
      return trpcMutation.updateObligationAssessmentResult(variables);
    }

    const result = await updateObligationAssessmentResultGraphQL({
      variables,
    });
    if (!result.data) {
      throw new Error('Failed to update obligation assessment result');
    }

    return result.data;
  };

  // Return appropriate state based on feature flag
  if (trpcEnabled) {
    return {
      updateObligationAssessmentResult,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    updateObligationAssessmentResult,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
